import React, { useMemo, useEffect } from 'react';
import BaseMap from './BaseMap';
import { scaleLinear } from 'd3-scale';
import { Box } from '@mui/material';
import { formatPercentage } from '../utils/mapUtils';
import { foodInsecurityScale } from '../utils/colorScales';
import { fipsToStateName } from '../utils/fipsUtils';

const FoodInsecurityMap = ({ data }) => {
  // Memoized color scale
  const colorScale = useMemo(() => scaleLinear()
    .domain(foodInsecurityScale.domain)
    .range(foodInsecurityScale.range), []);

  // Convert data to CountyMap-style format
  const processedData = useMemo(() => {
    return data?.map(item => ({
      fips: String(item.fips).padStart(5, '0'),
      foodInsecurityRate: item.foodInsecurityRate,
      countyName: item.countyName,
      stateCode: item.stateCode
    })) || [];
  }, [data]);

  // Update getFillColor to use processedData
  const getFillColor = (geo, directMatch = null) => {
    // If we already have a direct match from BaseMap's matching logic, use it
    if (directMatch) {
      return directMatch.foodInsecurityRate !== undefined 
        ? colorScale(directMatch.foodInsecurityRate)
        : '#EEEEEE';
    }
    
    // Otherwise, do the matching here
    const fips = String(geo.id).padStart(5, '0');
    const countyData = processedData.find(d => d.fips === fips);
    
    return countyData?.foodInsecurityRate !== undefined 
      ? colorScale(countyData.foodInsecurityRate)
      : '#EEEEEE';
  };

  // Create legend data using the foodInsecurityScale
  const legendColors = useMemo(() => 
    foodInsecurityScale.range,
  []);

  const legendValues = useMemo(() => [
    '0-10%',
    '10-20%',
    '20-30%',
    '30-40%',
    '40%+'
  ], []);

  // Updated tooltip handler with correct percentage formatting
  const handleMouseMove = (event, geo, countyData) => {
    const countyName = geo.properties?.name || 'Unknown';
    const stateFips = geo.id ? String(geo.id).substring(0, 2) : '';
    const stateName = fipsToStateName[stateFips] || '';
    
    // Check if we have a valid food insecurity rate
    if (!countyData?.foodInsecurityRate) return 'No data available';
    
    // Format the percentage correctly based on its magnitude
    const formattedRate = countyData.foodInsecurityRate > 1 ? 
      `${Math.round(countyData.foodInsecurityRate * 10) / 10}%` : 
      `${Math.round(countyData.foodInsecurityRate * 1000) / 10}%`;
    
    return `
      <strong>${countyName} County${stateName ? `, ${stateName}` : ''}</strong><br/>
      Food Insecurity Rate: ${formattedRate}
    `;
  };

  // Add data validation
  useEffect(() => {
    console.log('[FoodInsecurityMap] Processed data sample:', {
      inputFips: data?.[0]?.fips,
      processedFips: processedData?.[0]?.fips,
      geoFipsExample: "46121",
      match: processedData?.some(d => d.fips === "46121")
    });
  }, [processedData]);

  return (
    <BaseMap
      data={data}
      getFillColor={getFillColor}
      onHover={(e, geo, countyData) => handleMouseMove(e, geo, countyData)}
      legendTitle="Food Insecurity Rate"
      legendColors={legendColors}
      legendValues={legendValues}
    />
  );
};

export default FoodInsecurityMap; 